import ChecklistCollection from '@repo/datalayer/store/models/ChecklistCollection'
import { observer } from 'mobx-react-lite'
import { useRouter } from '../../hooks/router'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import {
  RotateCcw,
  CheckCircle2,
  History,
  Calendar,
} from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'

export const RecentChecklists = observer(function RecentChecklists({
  checklists,
}: {
  checklists: ChecklistCollection
}) {
  const router = useRouter()
  const { t } = useTranslation()

  if (checklists.items.length === 0) {
    return (
      <div data-test-id="recent-checklists-section" className="mb-10">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 rounded-xl bg-success/10">
            <History className="w-5 h-5 text-success" />
          </div>
          <h2 className="text-xl font-bold">{t('home.history')}</h2>
        </div>
        <div className="bg-card rounded-2xl border-2 border-dashed">
          <EmptyState
            icon={CheckCircle2}
            title="No completed checklists yet"
            description="Completed checklists will appear here"
          />
        </div>
      </div>
    )
  }

  return (
    <div data-test-id="recent-checklists-section" className="mb-10">
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2 rounded-xl bg-success/10">
          <History className="w-5 h-5 text-success" />
        </div>
        <h2 className="text-xl font-bold">{t('home.history')}</h2>
      </div>
      <div
        className={`grid gap-4 ${checklists.items.length > 1 ? 'sm:grid-cols-2' : ''}`}
      >
        {checklists.items.map((checklist) => (
          <div
            className="group relative bg-card rounded-2xl overflow-hidden cursor-pointer shadow-card transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
            data-test-id="completed-checklist-card"
            data-checklist-name={checklist.name}
            key={checklist.id}
            onClick={() => {
              router.navigateToChecklist(checklist.id)
            }}
          >
            {/* Gradient accent bar */}
            <div className="h-1.5 bg-success" />

            <div className="p-5">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-success text-success-foreground flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-lg leading-tight truncate mb-2">
                    {checklist.name}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {checklist.completed_at?.toLocaleDateString(undefined, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 flex-shrink-0"
                  onClick={async (e) => {
                    e.stopPropagation()
                    e.preventDefault()

                    checklist.rerun().then((newChecklist) => {
                      router.navigateToChecklist(newChecklist.id)
                    })
                  }}
                >
                  <RotateCcw size={14} className="mr-1.5" />
                  {t('checklists.rerun')}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
})
